import { graphql } from '@/generated/gql'
import { useQuery } from '@apollo/client'

import { differenceInCalendarDays, endOfDay, parseISO, startOfDay, subDays } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { useMemo } from 'react'

import type { CanteenRatingItem } from './types'

/** An item is offered from the day after delivery until a week later. */
const MIN_AGE_DAYS = 1
const MAX_AGE_DAYS = 7

const ITEM_LIMIT = 100

const itemsQuery = graphql(`
  query CanteenRatingBanner_ItemsQuery($input: CCItemsAwaitingReviewInput!) {
    me {
      id
      ccItemsAwaitingReview(input: $input) {
        rows {
          id
          name
          meal
          date
          accountLocation {
            id
            timezone
          }
          vendor {
            id
            companyName
          }
          liveMenuItem {
            coreLiveItem {
              _id
              image
            }
          }
        }
      }
    }
  }
`)

/** Age in days, counted in the canteen's own timezone rather than the browser's. */
const ageInCanteenDays = (date: string, timezone: string, now: Date) => {
  const mealDay = formatInTimeZone(new Date(date), timezone, 'yyyy-MM-dd')
  const todayThere = formatInTimeZone(now, timezone, 'yyyy-MM-dd')

  return differenceInCalendarDays(parseISO(todayThere), parseISO(mealDay))
}

export const useCanteenRatingItems = (userId?: string) => {
  // A day either side of the window; each item is then gated precisely below.
  const input = useMemo(() => {
    const now = new Date()

    return {
      periodStart: startOfDay(subDays(now, MAX_AGE_DAYS + 1)).toISOString(),
      periodEnd: endOfDay(now).toISOString(),
      limit: ITEM_LIMIT,
      offset: 0,
    }
  }, [])

  const { data, loading, error, refetch } = useQuery(itemsQuery, {
    variables: { input },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  })

  const items = useMemo<CanteenRatingItem[]>(() => {
    const rows = data?.me?.ccItemsAwaitingReview.rows ?? []
    const now = new Date()

    return rows
      .filter((row) => {
        const age = ageInCanteenDays(row.date, row.accountLocation.timezone, now)

        return age >= MIN_AGE_DAYS && age <= MAX_AGE_DAYS
      })
      .map((row) => ({
        id: row.id,
        name: row.name,
        meal: row.meal,
        date: row.date,
        timezone: row.accountLocation.timezone,
        vendorId: row.vendor.id,
        vendorName: row.vendor.companyName,
        imageUrl: row.liveMenuItem.coreLiveItem?.image ?? null,
      }))
  }, [data])

  return { items, loading, error, refetch }
}
